import * as React from 'react';
import { useConstant } from '@workwave-tidal/core/hooks'
import { type PropsWithChildren, createContext, useContext, useReducer } from 'react'

// ------------------------------
// READ context
// ------------------------------

type ScrollbarContext = {
  scrollToBottom: () => void
}

const defaultScrollbarContext = {
  scrollToBottom: () => {},
  api: null,
}

export const scrollbarContext = createContext<ScrollbarContext>(defaultScrollbarContext)

// ------------------------------
// WRITE context
// ------------------------------

type ScrollbarApiContext = {
  registerScrollToBottom: (scrollToBottom: () => void) => void
}

export const scrollbarApiContext = createContext<ScrollbarApiContext>({
  registerScrollToBottom() {},
})

// ------------------------------
// Store Reducer
// ------------------------------

type ScrollbarAction = { type: 'SET_SCROLL_TO_BOTTOM'; scrollToBottom: () => void }

function scrollbarReducer(state: ScrollbarContext, action: ScrollbarAction): ScrollbarContext {
  switch (action.type) {
    case 'SET_SCROLL_TO_BOTTOM':
      if (state.scrollToBottom === action.scrollToBottom) return state
      return { ...state, scrollToBottom: action.scrollToBottom }

    default:
      return state
  }
}

// ------------------------------
// Component
// ------------------------------

export function ScrollApiProvider(props: PropsWithChildren) {
  const { children } = props

  const [ctx, dispatch] = useReducer(scrollbarReducer, defaultScrollbarContext)

  const ctxApi: ScrollbarApiContext = useConstant(() => {
    return {
      registerScrollToBottom: (scrollToBottom: () => void) => {
        dispatch({ type: 'SET_SCROLL_TO_BOTTOM', scrollToBottom })
      },
    }
  })

  return (
    <scrollbarApiContext.Provider value={ctxApi}>
      <scrollbarContext.Provider value={ctx}>{children}</scrollbarContext.Provider>
    </scrollbarApiContext.Provider>
  )
}

// ------------------------------
// Hooks
// ------------------------------

export function useScrollbarConfigureApi() {
  return useContext(scrollbarApiContext)
}

export function useScrollbarScrollToBottom() {
  return useContext(scrollbarContext).scrollToBottom
}
